import axios from 'axios';

// Configuración base de la API
// Llamadas directas al backend (más rápido, menos latencia)
// NOTA: Requiere CORS configurado correctamente en api.appagustinos.es
const API_BASE_URL = 'https://api.appagustinos.es/api';

// Crear instancia de axios
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token automáticamente
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('cms_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para manejar errores de autenticación
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado o inválido
      localStorage.removeItem('cms_token');
      localStorage.removeItem('cms_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Funciones de autenticación
export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },
  
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Funciones de artículos
export const articlesAPI = {
  getSections: async () => {
    const response = await api.get('/articles/sections');
    return response.data;
  },
  
  getStats: async () => {
    const response = await api.get('/articles/stats');
    return response.data;
  },
  
  // CMS - Obtener lista de artículos
  getArticles: async (section = null, limit = 900, offset = 0) => {
    let url = `/articles/cms?limit=${limit}&offset=${offset}`;
    
    // Agregar parámetro section si se especifica
    if (section) {
      url += `&section=${encodeURIComponent(section)}`;
    }
    
    const response = await api.get(url);
    return response.data;
  },
  
  // CMS - Crear nuevo artículo
  createArticle: async (articleData) => {
    const response = await api.post('/articles/cms', articleData);
    return response.data;
  },
  
  // CMS - Obtener artículo por ID para editar
  getArticleById: async (id) => {
    const response = await api.get(`/articles/cms/${id}`);
    return response.data;
  },
  
  // CMS - Actualizar artículo
  updateArticle: async (id, articleData) => {
    const response = await api.put(`/articles/cms/${id}`, articleData);
    return response.data;
  },
  
  // CMS - Eliminar artículo
  deleteArticle: async (id) => {
    const response = await api.delete(`/articles/cms/${id}`);
    return response.data;
  },
};

// Funciones de galería de medios
export const mediaAPI = {
  // Obtener lista de imágenes de Bunny CDN
  getImages: async (folder = 'images', page = 1, limit = 20) => {
    const response = await api.get(`/media/list?folder=${folder}&page=${page}&limit=${limit}`);
    return response.data;
  },
  
  // Buscar imágenes en Pexels
  searchPexels: async (query, page = 1, per_page = 20) => {
    const response = await api.get(`/pexels/search?query=${encodeURIComponent(query)}&page=${page}&per_page=${per_page}`);
    return response.data;
  },
  
  // Descargar imagen de Pexels y subir a Bunny CDN
  downloadFromPexels: async (imageData) => {
    const response = await api.post('/pexels/download', imageData);
    return response.data;
  },
};

// Funciones de calendario
export const calendarAPI = {
  // CMS - Obtener eventos del calendario
  getEvents: async (eventType = null, year = null, month = null, limit = 500, offset = 0) => {
    let url = `/calendar/cms?limit=${limit}&offset=${offset}`;
    
    if (eventType) {
      url += `&event_type=${encodeURIComponent(eventType)}`;
    }
    
    if (year && month) {
      url += `&year=${year}&month=${month}`;
    }
    
    const response = await api.get(url);
    return response.data;
  },
  
  // CMS - Crear nuevo evento
  createEvent: async (eventData) => {
    const response = await api.post('/calendar/cms', eventData);
    return response.data;
  },
  
  // CMS - Obtener evento por ID
  getEventById: async (id) => {
    const response = await api.get(`/calendar/cms/${id}`);
    return response.data;
  },
  
  // CMS - Actualizar evento
  updateEvent: async (id, eventData) => {
    const response = await api.put(`/calendar/cms/${id}`, eventData);
    return response.data;
  },
  
  // CMS - Eliminar evento
  deleteEvent: async (id) => {
    const response = await api.delete(`/calendar/cms/${id}`);
    return response.data;
  },
  
  // Estadísticas del calendario
  getStats: async () => {
    const response = await api.get('/calendar/stats');
    return response.data;
  },
  
  // Público - Obtener eventos por mes (para preview)
  getEventsByMonth: async (year, month) => {
    const response = await api.get(`/calendar/events/month/${year}/${month}`);
    return response.data;
  },

  // CMS - Importación masiva (array de eventos, omite duplicados)
  bulkImport: async (events) => {
    const response = await api.post('/calendar/cms/bulk', { events });
    return response.data;
  },
};

// Funciones de notificaciones push
export const notificationsAPI = {
  // Obtener estadísticas de notificaciones y dispositivos
  getStats: async () => {
    const response = await api.get('/notifications/stats');
    return response.data;
  },

  // Listar historial de notificaciones
  getNotifications: async (page = 1, limit = 20, status = null) => {
    let url = `/notifications?page=${page}&limit=${limit}`;
    if (status) url += `&status=${status}`;
    const response = await api.get(url);
    return response.data;
  },

  // Enviar notificación inmediata
  sendNotification: async (data) => {
    const response = await api.post('/notifications/send', data);
    return response.data;
  },

  // Programar notificación futura
  scheduleNotification: async (data) => {
    const response = await api.post('/notifications/schedule', data);
    return response.data;
  },

  // Cancelar notificación programada
  cancelNotification: async (id) => {
    const response = await api.delete(`/notifications/${id}`);
    return response.data;
  },

  // Listar dispositivos registrados
  getDevices: async (page = 1, limit = 20, platform = null) => {
    let url = `/notifications/devices?page=${page}&limit=${limit}`;
    if (platform) url += `&platform=${platform}`;
    const response = await api.get(url);
    return response.data;
  },

  // --- Reglas de auto-notificación ---

  // Listar reglas de auto-notificación
  getAutoRules: async () => {
    const response = await api.get('/notifications/auto-rules');
    return response.data;
  },

  // Crear regla de auto-notificación
  createAutoRule: async (data) => {
    const response = await api.post('/notifications/auto-rules', data);
    return response.data;
  },

  // Actualizar regla (activar/desactivar, cambiar target)
  updateAutoRule: async (id, data) => {
    const response = await api.put(`/notifications/auto-rules/${id}`, data);
    return response.data;
  },

  // Eliminar regla
  deleteAutoRule: async (id) => {
    const response = await api.delete(`/notifications/auto-rules/${id}`);
    return response.data;
  },
};

// Lemas mensuales gestionados desde el CMS.
export const mottosAPI = {
  // Público: lista todos los lemas de un año.
  listByYear: async (year) => {
    const response = await api.get(`/mottos?year=${year}`);
    return response.data;
  },
  // CMS: upsert de un lema (crea o actualiza).
  upsert: async (year, month, text) => {
    const response = await api.post('/mottos/cms', { year, month, text });
    return response.data;
  },
  // CMS: elimina un lema.
  remove: async (year, month) => {
    const response = await api.delete(`/mottos/cms/${year}/${month}`);
    return response.data;
  }
};

export default api;